import { useDailyBriefing } from '@/hooks/use-daily-briefing';
import { useAIHealthCompanionStore } from '@/stores/ai-health-companion-store';
import { GlassCard } from '@/components/ui/glass-card';
import { cn } from '@/lib/utils';
import {
  Sparkles,
  Activity,
  FileText,
  Calendar,
  Pill,
  Trophy,
  Shield,
  ArrowRight,
  Bot
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';

const severityStyles = {
  info: 'border-l-blue-500 bg-blue-500/5',
  positive: 'border-l-green-500 bg-green-500/5',
  warning: 'border-l-amber-500 bg-amber-500/5',
  critical: 'border-l-red-500 bg-red-500/5',
};

const highlightIcons: Record<string, LucideIcon> = {
  'vital-trend': Activity,
  'test-result': FileText,
  'appointment': Calendar,
  'medication': Pill,
  'achievement': Trophy,
  'screening': Shield,
};

const riskScoreColors = {
  low: 'bg-green-100 text-green-800 border-green-300',
  medium: 'bg-amber-100 text-amber-800 border-amber-300',
  high: 'bg-red-100 text-red-800 border-red-300',
};

export default function AIGreetingCard() {
  const { briefing, isLoading } = useDailyBriefing();
  const togglePanel = useAIHealthCompanionStore((state) => state.togglePanel);
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <GlassCard variant="elevated" className="w-full">
        <div className="space-y-6 p-6">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-full bg-gray-200 animate-pulse" />
            <div className="flex-1 space-y-3">
              <div className="h-8 w-3/4 bg-gray-200 rounded animate-pulse" />
              <div className="h-4 w-full bg-gray-200 rounded animate-pulse" />
              <div className="h-4 w-5/6 bg-gray-200 rounded animate-pulse" />
            </div>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="min-w-[240px] h-32 bg-gray-200 rounded-lg animate-pulse" />
            ))}
          </div>
          <div className="flex items-center justify-between">
            <div className="h-10 w-32 bg-gray-200 rounded animate-pulse" />
            <div className="h-6 w-24 bg-gray-200 rounded-full animate-pulse" />
          </div>
        </div>
      </GlassCard>
    );
  }

  if (!briefing) {
    return null;
  }

  const handleHighlightAction = (path?: string) => {
    if (path) {
      navigate(path);
    }
  };

  return (
    <GlassCard variant="elevated" className="w-full">
      <div className="space-y-6 p-6">
        {/* Greeting Section */}
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
              <Sparkles className="h-6 w-6 text-white" />
            </div>
          </div>
          <div className="flex-1 space-y-2">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {briefing.greeting}
            </h2>
            <p className="text-base text-gray-600 dark:text-gray-300">
              {briefing.summary}
            </p>
          </div>
        </div>

        {/* Highlights Section */}
        {briefing.highlights && briefing.highlights.length > 0 && (
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-2 px-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
            {briefing.highlights.slice(0, 4).map((highlight, index) => {
              const Icon = highlightIcons[highlight.type] || Activity;
              return (
                <div
                  key={index}
                  className={cn(
                    "min-w-[240px] flex-shrink-0 rounded-lg border-l-4 p-4 space-y-2",
                    severityStyles[highlight.severity]
                  )}
                >
                  <div className="flex items-start gap-2">
                    <Icon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-sm text-gray-900 dark:text-white">
                        {highlight.title}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 line-clamp-2">
                        {highlight.description}
                      </p>
                    </div>
                  </div>
                  {highlight.actionLabel && (
                    <button
                      onClick={() => handleHighlightAction(highlight.actionPath)}
                      className="flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                    >
                      {highlight.actionLabel}
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Bottom Actions */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <button
            onClick={togglePanel}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium hover:from-blue-700 hover:to-purple-700 transition-all shadow-md hover:shadow-lg"
          >
            <Bot className="h-5 w-5" />
            Ask AI
          </button>

          {briefing.riskScore && (
            <div
              className={cn(
                "px-3 py-1.5 rounded-full border text-sm font-semibold",
                riskScoreColors[briefing.riskScore.category as keyof typeof riskScoreColors] || riskScoreColors.medium
              )}
            >
              Risk Score: {briefing.riskScore.overall}/100
            </div>
          )}
        </div>
      </div>
    </GlassCard>
  );
}
